/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { SettingsInterface } from '../data/interfaces';
import { getApplicationSettings } from '../data/utils';

declare global {
  interface Window {
    Settings: SettingsInterface;
  }
}

const defaultSettings: SettingsInterface = {
  enablePKCE: true,
  clientId: 'ZzHIXXuSuI1UXJOztKUB4gNzlaka',
  signInRedirectUrl: 'https://localhost:3000/sign-in',
  signOutRedirectUrl: 'https://localhost:3000/login',
  clientHost: 'https://localhost:3000',
  apimHost: 'https://localhost:3000',
  tokenExchangeUrl: 'https://apim.dv.choreo.dev/oauth2/token',
  apimApi: 'https://apim.dv.choreo.dev',
  apimGatewayHostSuffix: '.dv.choreoapis.dev',
  idpHost: '',
  apiServiceHost: 'https://localhost:3000',
  graphqlServiceHost: 'https://localhost:3000',
  graphqlResource: '/graphql',
  azureCDNOrigin: 'https://localhost:3000',
  devToolEnable: true,
  scopes: ['openid', 'email', 'profile','groups'],
  keyManager: 'Resident Key Manager',
  devportalContext: '/api/am/devportal/v2',
  apiServiceProxy: '/apim/proxy',
  userInfo: '/api/identity/user/v1.0/me',
  userOrganizations: '/apim/devportal/organizations',
  validateUserEndpoint: '/user-mgt/1.0.0/validate/user',
  addEnterpriseUserEndpoint: '/user-mgt/1.0.0/add-enterprise-user',
  tokenEndpoint: 'https://localhost:8243/token',
  revokeEndpoint: 'https://localhost:8243/revoke',
  tokenValidityPeriod: 3600,
  defaultAppThrottlingPolicy: 'Unlimited',
  defaultAppTokenType: 'JWT',
  googleFidp: 'google',
  githubFidp: 'github',
  microsoftFidp: 'microsoft',
  emailFidp: 'LOCAL',
  enableMicrosoftLogin: true,
  enableEmailLogin: false,
  oldGoogleFidp: 'google-choreo',
  oldGithubFidp: 'github-choreo',
  enterpriseFidp: 'EnterpriseIDP',
  overrideEndpoints: {
    authorizationEndpoint: 'https://dev.api.asgardeo.io/t/a/oauth2/authorize',
    tokenEndpoint: 'https://dev.api.asgardeo.io/t/a/oauth2/token',
    endSessionEndpoint: 'https://dev.api.asgardeo.io/t/a/oidc/logout',
  },
  wellKnownEndpoint: "",
  overrideWellEndpointConfig: true,
  tokenExchangeConfig: {
    clientId: 'choreodevportal',
    grantType: 'urn:ietf:params:oauth:grant-type:token-exchange',
    subjectTokenType: 'urn:ietf:params:oauth:token-type:jwt',
    requestedTokenType: 'urn:ietf:params:oauth:token-type:jwt',
    scope: 'apim:admin apim:subscribe environments:view_prod environments:view_dev apim:prod_key_manage apim:sand_key_manage urn:choreocontrolplane:customdomainapi:custom_domain_view urn:choreocontrolplane:usermanagement:user_view apim:app_manage',
  },
  grantTypes: [
    {
      id: 1,
      type: 'Client Credentials',
      value: 'client_credentials',
    },
  ],
  asgardeoGrantTypes: [
    {
      id: 1,
      type: 'Client Credentials',
      value: 'client_credentials',
    },
    {
      id: 2,
      type: 'Refresh Token',
      value: 'refresh_token',
    },
    {
      id: 3,
      type: "Password",
      value: "password",
    },
    {
      id: 4,
      type: "Implicit",
      value: "implicit",
    },
    {
      id: 5,
      type: "Code",
      value: "authorization_code",
    },
  ],
  enableInV2: false,
  choreoControlPlaneOrgHandleName: 'choreocontrolplane',
  azureInsightsKey: '',
  reactAppHotjarId: '',
  reactAppHotjarVersion : '',
  choreoSystemOrg: 'choreocontrolplane',
  choreoSystemHostSuffix: 'apis.preview-dv.choreo.dev',
  asgardeoConsoleUrl: 'https://dev.console.asgardeo.io',
  selfSignupEnabledOrgs: [],
  pdpDomainCustomizedOrgs: [],
  devportalHost: "localhost",
  isCustomDomain: false,
  customDomain: "",
  validateIDToken: true,
  permissionAdminPortalEnabled: false,
  permissionAdminPortalUrl: 'https://console.authz-dv.cloudservices.wso2.com/login',
  permissionPortalOrgIdParam: 'orgId',
  sandboxKeyGenerationEnabled: true,
  intelligentRoutingFeatureEnabled: false,
  devPortalIdPConfigurationFeatureEnabled: false,
  apiTryoutTestKeyEnabled: false,
  apiKeyFeatureEnabled: false,
};

const Settings = getApplicationSettings(window.Settings, defaultSettings);

export default Settings;
